import { Db2Database, Flash, Layers } from '@carbon/icons-react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import * as React from 'react';

import { NavbarAside } from 'bones/components/molecules/Navbar/NavbarAside';
import {
  PortalConnectionItem,
  usePortalConnectionItems,
} from 'bones/components/molecules/Portal/hooks/usePortalConnectionItem';
import { useVoiceboxSchema } from 'bones/components/molecules/Voicebox/context/VoiceboxSchema';
import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import {
  SPECIAL_NAMED_GRAPH_IRIS,
  useVoiceboxSettingsItems,
} from 'bones/components/molecules/Voicebox/hooks/useVoiceboxSettingsItems';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { Button } from 'bones/design-system/components/Button';
import { Toggle } from 'bones/design-system/components/controls/Toggle';
import { Selector } from 'bones/design-system/components/select/Selector';
import { useDidValueChange } from 'bones/utils/hooks/useDidValueChange';
import { isNotEqual } from 'bones/utils/isEqual';
import { noop } from 'bones/utils/noop';
import { createSelectItem } from 'bones/utils/select/createSelectItem';
import { SelectItem } from 'bones/utils/select/types';

export const VoiceboxSettingsAside: React.VFC = () => {
  const {
    config,
    copy,
    testIds,
    settingsAside: { isOpen, onSaveSettings },
  } = useVoiceboxSchema();
  const {
    databasesById,
    namedGraphsByDatabaseId,
    setNamedGraphFilter,
    setSettings,
    settings,
  } = useVoiceboxSettings();

  const { connectionItem } = usePortalConnectionItems(config.connectionIdx);

  const [localSettings, setLocalSettings] =
    React.useState<VoiceboxSettings>(settings);
  const didIsOpenChange = useDidValueChange(isOpen);
  React.useEffect(() => {
    if (didIsOpenChange) {
      setLocalSettings(settings);
    }
  }, [didIsOpenChange, isOpen, settings]);

  const didSettingsChange = isNotEqual(settings, localSettings);

  const { databaseItems, namedGraphItems, modelItems } =
    useVoiceboxSettingsItems({
      copy,
      databasesById,
      databaseId: localSettings.databaseId,
      namedGraphsByDatabaseId,
    });

  const handleSubmit = async () => {
    setSettings(localSettings);
    onSaveSettings?.(settings, localSettings);
  };

  const selectedDatabaseItem = localSettings.databaseId
    ? databaseItems.find((item) => item.value === localSettings.databaseId) ||
      createSelectItem(localSettings.databaseId, localSettings.databaseId)
    : null;

  const selectedNamedGraphItems = localSettings.namedGraphs
    .map(
      (namedGraph) =>
        namedGraphItems.find((item) => item.value === namedGraph) ||
        createSelectItem(namedGraph, namedGraph)
    )
    .filter((item): item is SelectItem => Boolean(item));

  const selectedModelItem = localSettings.model
    ? modelItems.find((item) => item.value === localSettings.model) ||
      createSelectItem(localSettings.model, localSettings.model)
    : null;

  let namedGraphsText = '';
  if (selectedNamedGraphItems.length) {
    namedGraphsText =
      selectedNamedGraphItems.length === 1
        ? selectedNamedGraphItems[0].text
        : copy.settingsAside.namedGraphs.selected(
            selectedNamedGraphItems.length
          );
  }

  const debouncedSetNamedGraphFilter = React.useMemo(
    () =>
      process.env.NODE_ENV === 'test'
        ? setNamedGraphFilter
        : /* istanbul ignore next */ debounce(setNamedGraphFilter, 500),
    [setNamedGraphFilter]
  );

  return (
    <NavbarAside
      className="vb-voicebox-settings-aside"
      title={copy.settingsAside.title}
    >
      <div className="vb-navbar-aside-divider" />
      <div className="vb-navbar-aside-content">
        <Selector.Single
          className="vb-navbar-aside-selector"
          disabled
          inputProps={{
            icon: Flash,
            label: copy.settingsAside.endpoint.label,
            size: 'lg-outside',
            testId: testIds.settingsAside.endpoint.selector,
            value: connectionItem?.text,
          }}
          items={[] as PortalConnectionItem[]}
          onSelect={noop}
          popoverProps={{
            popoverClassName: 'vb-navbar-aside-selector-popover',
          }}
          selected={connectionItem}
        />
      </div>
      <div className="vb-navbar-aside-content">
        <Selector.Single
          className="vb-navbar-aside-selector"
          inputProps={{
            icon: Db2Database,
            label: copy.settingsAside.database.label,
            placeholder: copy.settingsAside.database.placeholder,
            size: 'lg-outside',
            testId: testIds.settingsAside.database.selector,
            value: localSettings.databaseId,
          }}
          items={databaseItems}
          itemTestId={testIds.settingsAside.database.selectorItem}
          onSelect={(item) => {
            //  istanbul ignore else
            if (item && item.value && databasesById[item.value]) {
              const nextDatabaseId = item.value;
              const nextModels = databasesById[nextDatabaseId].models;
              const nextNamedGraphs = localSettings.namedGraphs.filter(
                (namedGraph) =>
                  SPECIAL_NAMED_GRAPH_IRIS.includes(namedGraph) ||
                  namedGraphsByDatabaseId[nextDatabaseId]?.[namedGraph]
              );
              setLocalSettings((prev) => ({
                ...prev,
                databaseId: nextDatabaseId,
                model: nextModels.includes(localSettings.model)
                  ? localSettings.model
                  : config.defaultSettings.model,
                namedGraphs: nextNamedGraphs.length
                  ? nextNamedGraphs
                  : config.defaultSettings.namedGraphs,
              }));
            }
          }}
          popoverProps={{
            popoverClassName: 'vb-navbar-aside-selector-popover',
          }}
          selected={selectedDatabaseItem}
        />
      </div>
      <div className="vb-navbar-aside-content">
        <Selector.Multi
          className="vb-navbar-aside-selector"
          filterTestId={testIds.settingsAside.namedGraphs.filter}
          inputProps={{
            icon: Layers,
            label: copy.settingsAside.namedGraphs.label,
            placeholder: copy.settingsAside.namedGraphs.placeholder,
            size: 'lg-outside',
            testId: testIds.settingsAside.namedGraphs.selector,
            value: namedGraphsText,
          }}
          items={namedGraphItems}
          itemTestId={testIds.settingsAside.namedGraphs.selectorItem}
          onQueryChange={(query) => {
            debouncedSetNamedGraphFilter(query);
          }}
          onSelect={(items) =>
            setLocalSettings((prev) => ({
              ...prev,
              namedGraphs: items.map((item) => item.value),
            }))
          }
          popoverProps={{
            popoverClassName: 'vb-navbar-aside-selector-popover',
          }}
          resetOnClose
          selected={selectedNamedGraphItems}
        />
      </div>
      <div className="vb-navbar-aside-content">
        <Selector.Single
          className="vb-navbar-aside-selector"
          inputProps={{
            label: copy.settingsAside.model.label,
            placeholder: copy.settingsAside.model.placeholder,
            size: 'lg-outside',
            testId: testIds.settingsAside.model.selector,
            value: localSettings.model,
          }}
          items={modelItems}
          itemTestId={testIds.settingsAside.model.selectorItem}
          onSelect={(item) => {
            //  istanbul ignore else
            if (item && item.value) {
              setLocalSettings((prev) => ({
                ...prev,
                model: item.value,
              }));
            }
          }}
          popoverProps={{
            popoverClassName: 'vb-navbar-aside-selector-popover',
          }}
          selected={selectedModelItem}
        />
      </div>
      <div className="vb-navbar-aside-content">
        <Toggle
          checked={localSettings.reasoning}
          id="vb-voicebox-settings-reasoning"
          onChange={() =>
            setLocalSettings((prev) => ({
              ...prev,
              reasoning: !prev.reasoning,
            }))
          }
          size="md"
          testId={testIds.settingsAside.reasoning}
        >
          {copy.settingsAside.reasoning.label}
        </Toggle>
      </div>
      <div className="vb-navbar-aside-divider" />
      <div
        className={classNames(
          'vb-navbar-aside-content',
          'vb-navbar-aside-content--right'
        )}
      >
        <div className="vb-voicebox-settings-aside-actions">
          {didSettingsChange ? (
            <Button
              onClick={() => setLocalSettings(settings)}
              size="md"
              testId={testIds.settingsAside.resetButton}
              variant="ghost-gray"
            >
              {copy.settingsAside.resetButton}
            </Button>
          ) : null}
          <Button
            disabled={!didSettingsChange}
            onClick={handleSubmit}
            size="md"
            testId={testIds.settingsAside.submitButton}
            variant="primary-accent"
          >
            {copy.settingsAside.submitButton}
          </Button>
        </div>
      </div>
    </NavbarAside>
  );
};
